/**
 * EmptyStates — designed empty states for each pillar.
 *
 * The plan (§Phase 10 testing block) says these MUST be designed before this
 * phase ships. Each renders inside the pillar card body where the chart band
 * + mini-grid + actions list normally would. The mood is reassuring rather
 * than "broken": small icon, kicker, headline, helpful next-step copy.
 *
 * Empty here means the pillar's primary metric is unavailable (no scan run
 * yet, no data collected, or zero items to surface). It is NOT the same as
 * "100% healthy" — a tenant with zero drifts still has chart history and a
 * positive metric, so the regular pillar UI handles that.
 */

import { ArrowRightIcon, CheckCircleIcon, ShieldCheckIcon, UserGroupIcon } from '@heroicons/react/24/outline'

type EmptyKind = 'compliance' | 'bpa' | 'identity'

interface Props {
  kind: EmptyKind
  /** Optional CTA href — wires up "Run a scan" / "Configure" buttons. */
  ctaHref?: string
}

const COPY: Record<EmptyKind, {
  kicker: string
  headline: string
  body: string
  ctaLabel: string
  Icon: typeof CheckCircleIcon
  /** Tailwind tint utility for the icon background. */
  tintClass: string
  /** Tailwind text class for the icon. */
  iconClass: string
}> = {
  compliance: {
    kicker: 'No drift signal yet',
    headline: 'Run a baseline scan to start tracking drift.',
    body:
      'Once a scan completes you will see the 30-day drift trend, active drifts by workload, and the items that need review here.',
    ctaLabel: 'Run baseline scan',
    Icon: CheckCircleIcon,
    tintClass: 'bg-primary-500/15',
    iconClass: 'text-primary-500',
  },
  bpa: {
    kicker: 'No framework data yet',
    headline: 'Assign a framework to start scoring best-practice posture.',
    body:
      'MCSB, EIDSCA, NIST, SCuBA, and CIS are all available. Pick one and Guardian evaluates your tenant against every control inside it.',
    ctaLabel: 'Open analyzer',
    Icon: ShieldCheckIcon,
    tintClass: 'bg-secondaryBlue-300/30',
    iconClass: 'text-secondaryBlue-700 dark:text-secondaryBlue-300',
  },
  identity: {
    kicker: 'No identity data yet',
    headline: 'Connect Entra ID to surface MFA hygiene.',
    body:
      'After the first user-insights refresh, Guardian shows MFA-method mix, dormant accounts, and license waste — with the people who need attention most listed by name.',
    ctaLabel: 'Refresh user insights',
    Icon: UserGroupIcon,
    tintClass: 'bg-secondarySand-300/40',
    iconClass: 'text-secondarySand-700 dark:text-secondarySand-300',
  },
}

export default function PillarEmptyState({ kind, ctaHref }: Props) {
  const { kicker, headline, body, ctaLabel, Icon, tintClass, iconClass } = COPY[kind]

  return (
    <div className="flex flex-1 flex-col items-start justify-center py-8">
      <div
        className={`grid h-12 w-12 place-items-center rounded-xl ${tintClass}`}
        aria-hidden
      >
        <Icon className={`h-6 w-6 ${iconClass}`} />
      </div>
      <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        {kicker}
      </div>
      <h4 className="mt-2 max-w-[22ch] font-display text-[18px] font-bold leading-[1.25] tracking-[-0.01em] text-slate-100 dark:text-slate-100">
        {headline}
      </h4>
      <p className="mt-3 max-w-[34ch] text-[13px] leading-[1.55] text-slate-400">
        {body}
      </p>

      {ctaHref && (
        <a
          href={ctaHref}
          className="
            mt-5 inline-flex items-center gap-2 border-b-2 border-primary-500
            pb-1 text-[11px] font-bold uppercase tracking-[0.16em]
            text-slate-100 dark:text-slate-100
            transition-[gap] duration-200 hover:gap-3
          "
        >
          {ctaLabel}
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  )
}

/**
 * Convenience wrappers — name the empty state at the call site for readability.
 */
export function ComplianceEmpty({ ctaHref }: { ctaHref?: string }) {
  return <PillarEmptyState kind="compliance" ctaHref={ctaHref} />
}
export function BpaEmpty({ ctaHref }: { ctaHref?: string }) {
  return <PillarEmptyState kind="bpa" ctaHref={ctaHref} />
}
export function IdentityEmpty({ ctaHref }: { ctaHref?: string }) {
  return <PillarEmptyState kind="identity" ctaHref={ctaHref} />
}
